import {
  callHostTool,
  listenToHostToolsUpdates,
  requestHostTools,
} from '@trimble-agentic-external-npm-local/agentic-platform-sdk-iframe-typescript';
import type {
  HostToolDeclaration,
  Tool,
} from '@trimble-agentic-internal-npm-local/agentic-chat-ui';
import { useEffect, useMemo, useState } from 'react';

const EMPTY_TOOLS: Tool[] = [];

/**
 * Bridges host tool DEFINITIONS received from the outer host page into the
 * core chat lib's `hostTools` prop. Definitions arrive over postMessage;
 * execution is forwarded back to the host via `callHostTool`.
 */
export const useHostTools = (): HostToolDeclaration[] => {
  const [definitions, setDefinitions] = useState<Tool[]>(EMPTY_TOOLS);

  useEffect(() => {
    let active = true;
    let receivedUpdate = false;

    void requestHostTools()
      .then(tools => {
        // Ignore the initial snapshot if a newer push already arrived, so a
        // late discovery resolution can't roll back the catalogue.
        if (active && !receivedUpdate) {
          setDefinitions(tools);
        }
      })
      .catch((error: unknown) => {
        console.error('Failed to request host tools', error);
      });

    const unsubscribe = listenToHostToolsUpdates(tools => {
      receivedUpdate = true;
      if (active) {
        setDefinitions(tools);
      }
    });

    return () => {
      active = false;
      unsubscribe();
    };
  }, []);

  return useMemo<HostToolDeclaration[]>(
    () =>
      definitions.map(definition => ({
        definition,
        callback: (args: Record<string, unknown>) =>
          callHostTool(definition.name, args),
      })),
    [definitions]
  );
};
